/**
 * WebSocketProvider — React context for shared WebSocket connection.
 *
 * Wraps the app with a single WebSocket connection. Child components
 * can subscribe to channels via the useWsChannel hook, which
 * automatically cleans up on unmount.
 */

import React, { createContext, useContext, useCallback, useRef, useEffect } from 'react'
import { useWebSocket, type WebSocketMessage } from '@/hooks/useWebSocket'
import { useQueryClient } from '@tanstack/react-query'

// ---- Context types ----

type ChannelCallback = (data: unknown) => void

interface WebSocketContextValue {
    /** Whether the WebSocket is connected */
    isConnected: boolean
    /** Subscribe to a channel — returns unsubscribe function */
    subscribe: (channel: string, callback: ChannelCallback) => () => void
}

const WebSocketContext = createContext<WebSocketContextValue | null>(null)

// ---- Provider ----

export function WebSocketProvider({ children }: { children: React.ReactNode }) {
    // Map: channel -> Set<callback>
    const listenersRef = useRef<Map<string, Set<ChannelCallback>>>(new Map())
    const queryClient = useQueryClient()

    const handleMessage = useCallback((msg: WebSocketMessage) => {
        // Dispatch to channel listeners
        const cbs = listenersRef.current.get(msg.channel)
        if (cbs) {
            for (const cb of cbs) {
                try {
                    cb(msg.data)
                } catch {
                    // Ignore listener errors
                }
            }
        }

        // ── Auto-update React Query cache for known channels ──
        if (msg.channel === 'alerts:overview') {
            queryClient.setQueryData(['ws', 'alerts', 'overview'], msg.data)
        } else if (msg.channel === 'orderbook:tracked') {
            queryClient.setQueryData(['ws', 'orderbook', 'tracked'], msg.data)
        } else if (msg.channel === 'health') {
            queryClient.setQueryData(['ws', 'health'], msg.data)
        }
    }, [queryClient])

    // Derive channel list from active listeners
    const channelList = useRef<string[]>([])

    const {
        isConnected,
        subscribe: wsSubscribe,
        unsubscribe: wsUnsubscribe,
    } = useWebSocket({
        channels: channelList.current,
        onMessage: handleMessage,
        enabled: true,
    })

    const subscribe = useCallback((channel: string, callback: ChannelCallback) => {
        // Add listener
        if (!listenersRef.current.has(channel)) {
            listenersRef.current.set(channel, new Set())
            // First listener for this channel — subscribe on WebSocket
            wsSubscribe(channel)
            channelList.current = Array.from(listenersRef.current.keys())
        }
        listenersRef.current.get(channel)!.add(callback)

        // Return unsubscribe function
        return () => {
            const listeners = listenersRef.current.get(channel)
            if (listeners) {
                listeners.delete(callback)
                if (listeners.size === 0) {
                    listenersRef.current.delete(channel)
                    wsUnsubscribe(channel)
                    channelList.current = Array.from(listenersRef.current.keys())
                }
            }
        }
    }, [wsSubscribe, wsUnsubscribe])

    const value: WebSocketContextValue = {
        isConnected,
        subscribe,
    }

    return (
        <WebSocketContext.Provider value={value}>
            {children}
        </WebSocketContext.Provider>
    )
}

// ---- Consumer hooks ----

/** Access the WebSocket context. */
export function useWsContext() {
    const ctx = useContext(WebSocketContext)
    if (!ctx) {
        throw new Error('useWsContext must be used within a WebSocketProvider')
    }
    return ctx
}

/**
 * Subscribe to a specific WebSocket channel.
 * Automatically unsubscribes when the component unmounts.
 *
 * @example
 * useWsChannel('alerts:overview', (data) => {
 *   console.log('New alerts data:', data)
 * })
 */
export function useWsChannel(channel: string, callback: ChannelCallback) {
    const { subscribe } = useWsContext()
    const callbackRef = useRef(callback)
    callbackRef.current = callback

    useEffect(() => {
        const unsub = subscribe(channel, (data) => callbackRef.current(data))
        return unsub
    }, [channel, subscribe])
}

/**
 * Returns true if the WebSocket is connected.
 */
export function useWsConnected(): boolean {
    const { isConnected } = useWsContext()
    return isConnected
}
